import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import SEO, { personSchema, orgSchema, locationSchema, faqSchema, websiteSchema, breadcrumbSchema, videoSchema } from "../../lib/seo";
import { IMG, MEDIA, STATS, LOCATIONS, hgLink, trackOutbound, HG_TRIAL_PATH } from "../../data/site";
import { PROGRAMS } from "../../data/programs";
import { GLOBAL_FAQS } from "../../data/content";
import { Reveal, Eyebrow, TrialButton, GhostButton, Marquee, StatBar, FAQBlock, SectionShell, VideoPlayer } from "../../components/shared";
import { ProgramFinderQuiz, ScheduleFilterable, ReviewCarousel, CoachCards, MembershipCards, LocationCards } from "../../components/interactive";

const HERO_LINES = ["Real Martial Arts.", "Taught by Someone", "Who Did It."];

const introVideoSchema = () =>
  videoSchema({
    name: "Meet the Mezger Martial Arts Program at Hidden Gym",
    description: "Introduction to the Mezger Martial Arts program at Hidden Gym — Richardson training footage and archival competition footage of 6× world champion Guy Mezger, covering Muay Thai, boxing, BJJ, wrestling, and MMA coaching.",
    path: MEDIA.videos.mmaIntro.src,
    poster: MEDIA.videos.mmaIntro.poster,
    uploadDate: "2026-09-15",
    duration: MEDIA.videos.mmaIntro.duration,
  });

const KineticHero = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  return (
    <section ref={ref} className="relative min-h-[92vh] flex items-end bg-[#0A0B0D] text-white overflow-hidden grain">
      <motion.div style={reduce ? {} : { y }} className="absolute inset-0">
        <img
          src={MEDIA.photos.bjjRichardson}
          alt="Brazilian Jiu-Jitsu class training at Mezger Martial Arts inside Hidden Gym Richardson"
          className="w-full h-full object-cover opacity-45"
          data-testid="hero-photo"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/40 to-[#0A0B0D]/70" />
      </motion.div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 pt-40 w-full">
        <motion.p initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="font-mono text-xs sm:text-sm tracking-[0.25em] uppercase text-neutral-400">
          Mezger Martial Arts at Hidden Gym · Richardson & North Dallas
        </motion.p>
        <h1 className="mt-4 font-display font-black uppercase tracking-tight leading-[0.92] text-5xl sm:text-6xl lg:text-8xl" data-testid="hero-headline">
          {HERO_LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span className="block" initial={reduce ? false : { y: "110%" }} animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.15 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}>
                {i === 2 ? <>Who <span className="text-neutral-400">Did It.</span></> : line}
              </motion.span>
            </span>
          ))}
        </h1>
        <motion.p initial={reduce ? false : { opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.7 }}
          className="mt-6 max-w-2xl text-base md:text-lg text-white/75">
          Muay Thai, Boxing, Brazilian Jiu-Jitsu, Wrestling, and Kids programs led by Guy Mezger and his black-belt coaching staff, inside Hidden Gym in Richardson and North Dallas.
        </motion.p>
        <motion.div initial={reduce ? false : { opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.7 }} className="mt-8 flex flex-wrap gap-4">
          <TrialButton campaign="home-hero" page="home" testId="hero-claim-trial-btn">Book a Free Trial Class</TrialButton>
          <GhostButton to="/schedule" testId="hero-schedule-btn" className="text-white border-white/40">See the Class Schedule</GhostButton>
        </motion.div>
        <motion.div initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.05 }} className="mt-5 flex flex-wrap gap-x-6 gap-y-2 font-mono text-xs uppercase tracking-widest">
          <Link to="/locations/richardson" className="text-white/60 hover:text-white underline underline-offset-4" data-testid="hero-location-richardson-link">■ Richardson · 235 N Central Expy</Link>
          <Link to="/locations/north-dallas" className="text-white/60 hover:text-white underline underline-offset-4" data-testid="hero-location-north-dallas-link">■ North Dallas · Preston & Frankford</Link>
        </motion.div>
        <motion.ul initial={reduce ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.15 }}
          className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs font-mono uppercase tracking-widest text-white/50" data-testid="hero-trust-strip">
          {["30+ Years Coaching", "4.9★ from 580+ Reviews", "All Levels · No Sparring Required", "Ages 5 to Adult", "2 North Texas Locations"].map((t) => (
            <li key={t} className="flex items-center gap-2"><span className="text-neutral-400">■</span>{t}</li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
};

const MANIFESTO = [
  { n: "01", title: "Technique Over Ego", body: "Nobody here has anything to prove. The best training rooms in the world are the calmest ones — that's how Guy trained with world champions, and it's how this gym runs. Leave the ego in the parking lot and you'll learn twice as fast." },
  { n: "02", title: "Safety First, Always", body: "No one is forced to spar. Not on day one, not ever. Contact is a choice you make when you're ready — and most members never make it, and get world-class training anyway. Thirty years, one rule: the gym is where you get better, not hurt." },
  { n: "03", title: "Respect On and Off the Mat", body: "Bow to the room's standards: partners protected, beginners welcomed, kids held to the same code as adults. Martial arts without respect is just violence with a schedule. That's not what we build here." },
];

export default function Home() {
  return (
    <>
      <SEO
        title="Mezger Martial Arts at Hidden Gym | Guy Mezger | Richardson & North Dallas"
        description="Real martial arts taught by former UFC & PRIDE fighter Guy Mezger. Muay Thai, Boxing, BJJ, Wrestling & Kids programs at Hidden Gym Richardson and North Dallas. First class free."
        schemas={[websiteSchema(), personSchema(), orgSchema(), introVideoSchema(), ...LOCATIONS.map(locationSchema), faqSchema(GLOBAL_FAQS), breadcrumbSchema([["Home", "/"]])]}
      />
      <KineticHero />
      <Marquee items={["6× World Champion", "Ultimate Fighting Champion", "3× King of Pancrase", "World Kickboxing Champion", "7th Degree Black Belt", "580+ Five-Star Reviews", "Richardson · North Dallas"]} />

      <SectionShell dark className="grain">
        <div className="grid lg:grid-cols-5 gap-10 items-center">
          <Reveal className="lg:col-span-2">
            <Eyebrow>Watch</Eyebrow>
            <h2 className="mt-2 font-display font-black uppercase tracking-tight text-3xl sm:text-4xl leading-none">Meet the<br />Mezger Program.</h2>
            <p className="mt-5 text-white/70 leading-relaxed">Richardson training footage and archival competition footage from Guy's career — what the program is, who teaches it, and what your first weeks look like. About two and a half minutes.</p>
            <div className="mt-7"><TrialButton campaign="home-intro-video" page="home" testId="intro-video-trial-btn">Book a Free Trial Class</TrialButton></div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-3">
            <VideoPlayer
              src={MEDIA.videos.mmaIntro.src}
              poster={MEDIA.videos.mmaIntro.poster}
              title="Meet the Mezger Martial Arts program at Hidden Gym"
              caption="Richardson training + archival competition footage"
              dark
              testId="home-intro-video"
            />
          </Reveal>
        </div>
      </SectionShell>

      <SectionShell>
        <Reveal><ProgramFinderQuiz /></Reveal>
      </SectionShell>

      <SectionShell dark className="relative grain">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <Eyebrow>The Philosophy</Eyebrow>
            <h2 className="mt-2 font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-none">A Fighter's Gym.<br />Built for Everyone.</h2>
            <p className="mt-6 text-white/75 leading-relaxed">
              Guy Mezger fought the best in the world when the sport was being invented — Ultimate Fighting Champion, three times crowned King of Pancrase in Japan, six world titles across four full-contact sports. He's spent the 30 years since teaching North Texas the way he wishes he'd been taught: technique before toughness, patience before pressure, and a room where the newest beginner is protected by the most experienced hand.
            </p>
            <p className="mt-4 text-white/75 leading-relaxed">
              <strong className="text-white">Mezger Martial Arts is the martial arts program at Hidden Gym.</strong> Your membership is a Hidden Gym membership, which includes full access to the gym floor, strength equipment, and open mat.
            </p>
            <div className="mt-8"><GhostButton to="/about" testId="about-guy-link" className="text-white border-white/40">Read Guy's Full Story</GhostButton></div>
          </Reveal>
          <div className="space-y-6">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.1}>
                <div className="border border-white/10 bg-[#131518] p-6 md:p-8 flex gap-6">
                  <span className="font-display font-black text-5xl text-white/30 leading-none">{m.n}</span>
                  <div>
                    <h3 className="font-display font-black uppercase text-xl">{m.title}</h3>
                    <p className="mt-2 text-white/65 text-sm leading-relaxed">{m.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="mt-14"><StatBar stats={STATS} /></div>
      </SectionShell>

      <SectionShell id="programs">
        <Reveal>
          <Eyebrow>Programs</Eyebrow>
          <h2 className="mt-2 font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl">Pick Your Discipline</h2>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PROGRAMS.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 4) * 0.07} className="h-full">
              <div className="group h-full flex flex-col bg-white border border-black/10 hover:border-[#0A0B0D] transition-colors overflow-hidden" data-testid={`program-card-${p.slug}`}>
                <Link to={`/programs/${p.slug}`} className="aspect-[4/3] overflow-hidden block">
                  <img src={p.image} alt={p.imageAlt} loading="lazy" className={`w-full h-full object-cover ${p.imagePos || "object-center"} group-hover:scale-105 transition-transform duration-500`} />
                </Link>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-display font-black uppercase text-xl leading-tight"><Link to={`/programs/${p.slug}`} className="hover:text-neutral-500">{p.name}</Link></h3>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-wider text-[#5A6270]">{p.level} · Ages {p.ages}</p>
                  <p className="mt-3 text-sm text-[#374151] leading-relaxed flex-1">{p.card}</p>
                  <Link to={hgLink(HG_TRIAL_PATH, `home-program-${p.slug}`)} onClick={() => trackOutbound("home", `program-${p.slug}`)} data-testid={`program-card-${p.slug}-trial-btn`}
                    className="mt-4 text-center bg-[#0A0B0D] hover:bg-neutral-200 hover:text-[#0A0B0D] text-white font-display font-bold uppercase tracking-wide py-3 transition-colors">
                    Book a Free Trial
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </SectionShell>

      <SectionShell dark>
        <Reveal>
          <Eyebrow>Why Train Here</Eyebrow>
          <h2 className="mt-2 font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl">Not a Franchise. A Lineage.</h2>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {[
            ["Coached by a former world champion", "Not a weekend-certified franchise instructor. Guy competed against the best on earth, and his coaches teach his system."],
            ["Real fitness, built in", "Conditioning, strength, and mobility are woven into every class — you get in shape by learning something worth knowing."],
            ["Practical self-defense", "Distance, control, and composure under pressure. Skills that work because they've been tested where it matters."],
            ["Confidence and discipline", "The kind that comes from earned skill — for adults under stress and kids finding their footing."],
            ["A serious, kind community", "People who take training seriously and treat each other well. The two are connected."],
            ["Full Hidden Gym access included", "One membership covers unlimited classes plus the whole gym floor, strength equipment, and open mat."],
          ].map(([t, b], i) => (
            <Reveal key={t} delay={(i % 3) * 0.08} className="h-full">
              <div className="bg-[#0A0B0D] p-7 h-full">
                <span className="font-display font-black text-neutral-400 text-2xl">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-2 font-display font-bold uppercase text-lg">{t}</h3>
                <p className="mt-2 text-white/60 text-sm leading-relaxed">{b}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </SectionShell>

      <SectionShell>
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Eyebrow>Your First Class</Eyebrow>
            <h2 className="mt-2 font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl">What to Expect</h2>
            <ol className="mt-8 space-y-5">
              {[
                "Book your free trial through Hidden Gym — takes two minutes.",
                "Arrive 15 minutes early, sign the waiver, meet the front desk.",
                "Wear athletic clothes. Gloves and pads are provided for trials.",
                "A coach walks you through warm-up, fundamentals, and drills at your level.",
                "No sparring on day one. Ever.",
                "Leave with a plan — and a membership offer from Hidden Gym.",
              ].map((s, i) => (
                <li key={i} className="flex gap-4 items-start">
                  <span className="font-display font-black text-2xl text-neutral-400 leading-none w-8 shrink-0">{i + 1}</span>
                  <p className="text-[#374151]">{s} {i === 1 && <Link to="/waiver" className="underline hover:text-neutral-500">(read the waiver)</Link>}</p>
                </li>
              ))}
            </ol>
            <div className="mt-8"><TrialButton campaign="home-first-class" page="home" testId="first-class-trial-btn" /></div>
          </Reveal>
          <Reveal delay={0.15}>
            <img src={IMG.privateLesson} alt="One-on-one boxing pad coaching at Hidden Gym North Dallas" loading="lazy" className="w-full aspect-[4/3] object-cover border border-black/10" />
          </Reveal>
        </div>
      </SectionShell>

      <SectionShell id="schedule" className="bg-white">
        <Reveal>
          <Eyebrow>Schedule Preview</Eyebrow>
          <h2 className="mt-2 font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl">This Week on the Mats</h2>
        </Reveal>
        <div className="mt-8"><ScheduleFilterable compact /></div>
        <div className="mt-8"><TrialButton campaign="home-schedule" page="home" testId="schedule-reserve-trial-btn">Reserve Your Trial Spot</TrialButton></div>
      </SectionShell>

      <SectionShell>
        <Reveal>
          <Eyebrow>Membership</Eyebrow>
          <h2 className="mt-2 font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl">One Membership. The Whole Gym.</h2>
          <p className="mt-4 max-w-2xl text-[#374151]">Membership is handled by Hidden Gym and includes unlimited martial arts classes plus full facility access. Current offers below — every button takes you to Hidden Gym's signup.</p>
        </Reveal>
        <div className="mt-10"><MembershipCards /></div>
      </SectionShell>

      <SectionShell className="bg-white">
        <Reveal>
          <Eyebrow>Reviews</Eyebrow>
          <h2 className="mt-2 font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl">580+ People Said It Better</h2>
        </Reveal>
        <div className="mt-10"><ReviewCarousel /></div>
      </SectionShell>

      <SectionShell>
        <Reveal className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Eyebrow>The Coaches</Eyebrow>
            <h2 className="mt-2 font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl">Learn From People Who Lived It</h2>
          </div>
          <Link to="/coaches" className="underline font-bold hover:text-neutral-500" data-testid="all-coaches-link">All coaches →</Link>
        </Reveal>
        <div className="mt-10"><CoachCards /></div>
      </SectionShell>

      <SectionShell className="bg-white">
        <Reveal>
          <Eyebrow>Locations</Eyebrow>
          <h2 className="mt-2 font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl">Two Gyms. One Standard.</h2>
        </Reveal>
        <div className="mt-10"><LocationCards /></div>
      </SectionShell>

      <SectionShell>
        <Reveal>
          <Eyebrow>Questions</Eyebrow>
          <h2 className="mt-2 font-display font-black uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl">Asked Every Week</h2>
        </Reveal>
        <div className="mt-8 max-w-3xl"><FAQBlock faqs={GLOBAL_FAQS.slice(0, 8)} testPrefix="home-faq" /></div>
        <Link to="/faqs" className="mt-6 inline-block underline font-bold hover:text-neutral-500" data-testid="all-faqs-link">All FAQs →</Link>
      </SectionShell>

      <section className="relative bg-[#0A0B0D] text-white py-24 lg:py-32 overflow-hidden grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <Reveal>
            <h2 className="font-display font-black uppercase tracking-tight text-4xl sm:text-5xl lg:text-7xl leading-[0.95]">
              Thirty Years of Coaching.<br /><span className="text-neutral-400">Your First Class Is Free.</span>
            </h2>
            <div className="mt-10 flex justify-center">
              <TrialButton campaign="home-final" page="home" testId="final-cta-trial-btn" className="text-lg px-10 py-5">Book Your Free Trial at Hidden Gym</TrialButton>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
